import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectChange as MatSelectChange,
  MatLegacySelectModule as MatSelectModule,
} from '@angular/material/legacy-select';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FieldDef } from '@core/models';
import { LayoutService } from 'app/layout/layout.service';
import { moveRelatedToNestedFieldsToParent, orderNestedFields } from '@shared/functions';
import { SharedModule } from '@shared/shared.module';
import { tap, delay as delayRxjs } from 'rxjs/operators';
import { BehaviorSubject, noop, Observable, of, Subject } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { RelatedToType } from '@shared/enums';
import { typeTitleMap } from '@shared/models/type-title-map';
import { TsButtonModule, TsOverlayComponent, TsOverlayModule } from 'tsui';
import { MatListModule } from '@angular/material/list';
import { ReportingState } from 'app/modules/reporting/state/state';
import { SummaryFormula } from '@services/summary-formula.service';
import { ReportLayoutSummaryFormula } from 'app/modules/reporting/reporting.types';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';
import { FieldDefsState } from '@state/fields/state';

const REMOVE_PARENTHESES_REGEX = /\(([^)]+)\)/g;
// eslint-disable-next-line @typescript-eslint/naming-convention
const groupNames = { TimeSeries: 'Time Series', Deal: 'Deal' };
const tsFieldFilterArg = '(cf_name:';

@Component({
  selector: 'app-reporting-builder-field-selector',
  templateUrl: './field-selector.component.html',
  styleUrls: ['./field-selector.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatIconModule,
    MatOptionModule,
    MatSelectModule,
    TsOverlayModule,
    MatListModule,
    TsButtonModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldSelectorComponent extends OnDestroyMixin implements OnInit {
  optionsGroups = [];
  private _fields: FieldDef[];

  get fields(): FieldDef[] {
    return this._fields;
  }

  @Input() openSelect = false;
  @Input() canEdit = true;

  @Input() set fields(fields: FieldDef[]) {
    if (!this.isTimeSeries) {
      this._fields = orderNestedFields(moveRelatedToNestedFieldsToParent(fields));
    } else {
      this._fields = fields;
    }
  }
  @Input() isTimeSeries = false;
  @Input() isTimeSeriesGrouped = false;
  @Input() set selectedFields(value: string[]) {
    const tsFields = value.filter((x) => x.indexOf(tsFieldFilterArg) > -1);
    const defaultFields = value.filter((x) => x.indexOf(tsFieldFilterArg) === -1);

    this.setListGroups();

    const tempValue = [...tsFields, ...defaultFields];

    if (this.selectedFieldsSubject.value === null) {
      this.lastSelectedValue = tempValue;
    }

    this.selectedFieldsSubject.next(tempValue);
  }
  @Input() groupMode = false;
  @Input() relatedToLabel;
  @Input() fieldFormula: ReportLayoutSummaryFormula[];

  @Output() fieldsChanged = new EventEmitter<{ fields: string[]; forceUpdate?: boolean }>();

  @Output() selectOpenecChanged = new EventEmitter<boolean>();
  @Output() setFormulaToField = new EventEmitter<Partial<ReportLayoutSummaryFormula>>();

  @ViewChild('selectField', { static: false }) selectFieldElement: MatSelect;
  @ViewChild('overlayBox') overlayBox: TsOverlayComponent;

  selectedFields$: Observable<string[]>;
  searchField = new FormControl('');
  listGroups = [];
  hasSummaryFormulasOn = false;

  private selectedFieldsSubject = new BehaviorSubject<string[]>(null);
  private selectedFieldsForceSubject = new Subject<string[]>();
  private lastSelectedValue = [];
  private lastFieldClicked: FieldDef;

  constructor(
    private readonly layoutService: LayoutService,
    private readonly store: Store,
  ) {
    super();

    this.hasSummaryFormulasOn = this.store
      .selectSnapshot(AccountState.getAccount)
      ?.features.includes(FeatureFlag.SUMMARY_FORMULAS);
  }

  setListGroups(): void {
    this.listGroups = [...new Set(this.fields.map((field: any) => field.group))];
    if (this.isTimeSeries) {
      this.listGroups = this.listGroups.filter((x) => x.toLowerCase().trim() !== 'time series');
      this.listGroups.unshift('Time Series');
    }
  }

  ngOnInit(): void {
    if (this.groupMode) {
      this.setListGroups();
    }

    this.layoutService.emitCommon('openSelectField', null, 'subject');

    of(this.openSelect)
      .pipe(delayRxjs(10))
      .subscribe((res) => {
        if (res) {
          this.layoutService.emitCommon('openSelectField', true, 'subject');
        }
      });

    this.selectedFields$ = this.selectedFieldsSubject.asObservable().pipe(
      untilComponentDestroyed(this),
      tap((fields) => this.fieldsChanged.emit({ fields })),
    );

    this.selectedFieldsForceSubject
      .asObservable()
      .pipe(
        untilComponentDestroyed(this),
        tap((fields) => this.fieldsChanged.emit({ fields, forceUpdate: true })),
      )
      .subscribe(noop);

    this.layoutService
      .getCommon('openSelectField')
      .pipe(
        untilComponentDestroyed(this),
        tap(() => {
          this.selectFieldElement.open();
          this.selectFieldElement.openedChange.subscribe((res) => {
            this.selectOpenecChanged.emit(res);
          });
        }),
      )
      .subscribe(noop);
  }

  @Select(ReportingState.getSummaryFormulas) formulas$: Observable<SummaryFormula[]>;

  fieldSelectionOpenedChange(): void {
    this.searchField.setValue('');
  }

  fieldSelectionChange(event: MatSelectChange): void {
    let newState = [];

    if (event.value.length < this.lastSelectedValue.length) {
      newState = [...event.value];
      this.lastSelectedValue = newState;
      this.selectedFieldsSubject.next(newState);
      return;
    }

    const added = event.value.filter((x) => !this.lastSelectedValue.includes(x));

    if (this.isTimeSeries && added.every((x) => x.includes(tsFieldFilterArg))) {
      //@ts-ignore
      let lastIndex = this.lastSelectedValue.findLastIndex((x) => x.includes(tsFieldFilterArg));
      lastIndex = lastIndex === -1 ? this.lastSelectedValue.length : lastIndex + 1;
      this.lastSelectedValue.splice(lastIndex, 0, ...added);
      newState = [...this.lastSelectedValue];
    } else {
      newState = [...this.lastSelectedValue.map((x) => event.value.find((y) => y === x)).filter((x) => x), ...added];
    }

    this.lastSelectedValue = newState;
    this.selectedFieldsSubject.next(newState);
  }

  removeField(fieldName: string): void {
    this.selectedFieldsSubject.next(this.selectedFieldsSubject.value.filter((name) => name !== fieldName));
  }

  selectedFieldsFromGroup(selectedFields, group) {
    let fields: any = this.fields.filter(
      (field: any) => field.group.toLowerCase().trim() === group.toLowerCase().trim(),
    );

    const nestedFields = fields.reduce((acc, field) => {
      if (field.nestedFields && field.nestedFields.length > 0) {
        acc.push(...field.nestedFields);
      }
      return acc;
    }, []);

    fields = [...nestedFields, ...fields];

    const resultFields = [];

    // find fields in the same order as selected columns
    for (const field of selectedFields) {
      const fieldFound = fields.find((x) => x.name === field);
      if (fieldFound) {
        resultFields.push(fieldFound);
      }
    }

    return of(resultFields);
  }

  getFieldLabel(fieldName: string): string {
    return this.fields.find(({ name }) => name === fieldName)?.label;
  }

  drop(event: CdkDragDrop<string[]>, listGroup = null as string): void {
    if (listGroup === groupNames.Deal) {
      const tsFieldsCount = event.container.data.filter((x) => x.indexOf(tsFieldFilterArg) > -1).length;
      moveItemInArray(event.container.data, event.previousIndex + tsFieldsCount, event.currentIndex + tsFieldsCount);
    } else {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }
    const newState = [...event.container.data];
    this.lastSelectedValue = newState;
    this.selectedFieldsForceSubject.next(newState);
  }

  filterForTS(value: any[], filter: any): any[] {
    return value.filter((x) => {
      const field = this.fields.find((y) => y.name === x) as any;
      return field && field.group.toLowerCase().trim() === filter.toLowerCase().trim() ? x : null;
    });
  }

  removeDealFieldIfTSAlreadyExist(value, _): void {
    if (!this.isTimeSeries) return value;
    const tsFields = this.fields
      .filter((field) => field.name.includes('cf_name:'))
      .map((field) => field.name.replace(REMOVE_PARENTHESES_REGEX, ''));
    return value.filter(
      (optionValue) =>
        optionValue.group === 'Time Series' ||
        (optionValue.group === 'Project' && !tsFields.includes(optionValue.name)),
    );
  }

  getGroupLabel(name: string): string {
    if (name === RelatedToType.Trail) {
      return typeTitleMap[name];
    }

    if (name === 'related_to' && this.relatedToLabel) {
      return this.relatedToLabel;
    }

    let label;

    if ((label = this.store.selectSnapshot(ObjectsManagerState.get).find((x) => x.name === name)?.label)) {
      return label;
    }

    if ((label = this.store.selectSnapshot(FieldDefsState.getFieldDefs).find((x) => x.name === name)?.label)) {
      return label;
    }

    let result = name
      .replace(/cf_linked_/, '')
      .replace(/cf_/, '')
      .replace(/co_\d+_/, '')
      .split('.')[0]
      .split('_')
      .join(' ');

    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  hideDealName(fieldDef: FieldDef): boolean {
    return (
      fieldDef.name === 'title' &&
      fieldDef.related_to_type === RelatedToType.Project &&
      this.isTimeSeries &&
      this.isTimeSeriesGrouped
    );
  }

  canHaveFormula(fieldDef: FieldDef, fieldName: string = null): boolean {
    if (!this.hasSummaryFormulasOn) {
      return false;
    }

    if (fieldName) {
      fieldDef = this.fields.find((x) => x.name === fieldName);
    }

    return !fieldDef.usedForTimeSeries && ['number', 'decimal', 'percent', 'money'].includes(fieldDef.type);
  }

  hasFormula(selectedField: FieldDef, fieldName: string = null): boolean {
    if (fieldName) {
      selectedField = this.fields.find((x) => x.name === fieldName);
    }

    return !!this.fieldFormula?.find((x) => x.field === selectedField.name);
  }

  setFieldSelected(fieldDef: FieldDef, fieldName: string = null): void {
    if (fieldName) {
      fieldDef = this.fields.find((x) => x.name === fieldName);
    }

    this.lastFieldClicked = fieldDef;
  }

  formulaClick(formula: Partial<ReportLayoutSummaryFormula>): void {
    this.setFormulaToField.emit({ summary_formula_id: formula.id, field: this.lastFieldClicked.name });
    this.overlayBox.closeOverlay();
  }

  isFormulaSelected(formula: Partial<ReportLayoutSummaryFormula>): boolean {
    return !!this.fieldFormula?.find(
      (x) => x.summary_formula_id === formula.id && x.field === this.lastFieldClicked?.name,
    );
  }

  clearFormulaSelection(): void {
    this.setFormulaToField.emit({ _destroy: true, field: this.lastFieldClicked.name });
  }

  hasSearchMatchInGroup(searchCriteria: string, items: any[]): boolean {
    if (!searchCriteria) {
      return true;
    }

    if (items.length === 1 && items[0].nestedFields) {
      return !!items[0].nestedFields.find((x) => x.label?.toLowerCase().indexOf(searchCriteria.toLowerCase()) > -1);
    }

    return !!items.find((x) => x.label?.toLowerCase().indexOf(searchCriteria.toLowerCase()) > -1);
  }
}
